import './IntegrationFormZendesk.css';
import Input from '../../../Input/Input';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { clsx } from 'clsx';
import { Checkbox } from '../../../Checkbox/Checkbox';
import { EMAIL_REGEX } from '../../../../shared/constants';
import {
  useAddIntegrationMutation,
  useLazyGetZendeskConsentLinkQuery,
  useUpdateIntegrationMutation,
} from 'src/services';
import { parseSubdomain } from 'src/helpers/getSubdomainHelper';
import { Button } from 'src/components/ui/button';
import { Collapsible } from 'src/components/ui/collapsible';
import { Separator } from 'src/components/ui/separator';
import ArrowLargeIcon from 'src/assets/img/arrow-large-icon.svg';
import ChevronDownIcon from 'src/assets/img/chevron-down-icon.svg';
import ChevronDownDisabledIcon from 'src/assets/img/chevron-down-disabled-icon.svg';
import { setSelectedIntegration } from 'src/store/slices/integrationSlice';
import { useAppDispatch, useAppSelector } from 'src/store';
import { useNotification } from 'src/hooks/useNotification';
import { useLoader } from 'src/provider/LoaderProvider';
import { Spinner } from 'src/components/Spinner/Spinner';
import { useIntegrationAuthorizationPolling } from 'src/components/Integrations/useIntegrationAuthorizationPolling';
import type { SelectedIntegration } from 'src/store/slices/integrationSlice';
import type { IntegrationSubmitHandler } from 'src/components/Integrations/integrationFormTypes';

interface ZendeskFormValues {
  subdomain: string;
  auth_email: string;
  api_token: string;
  via_email: boolean;
  via_chat: boolean;
  credits: number;
}

const isLongerThanTwo = (value: string) => Boolean(value && value.length > 2);

export const IntegrationFormZendesk = ({
  integration,
  onSubmit,
}: {
  integration?: SelectedIntegration;
  onSubmit?: IntegrationSubmitHandler;
}) => {
  const { id } = useParams();
  const { selectedIntegration } = useAppSelector((s) => s.integrationSlices);
  const dispatch = useAppDispatch();
  const notification = useNotification();
  const loader = useLoader();
  // The add flow passes the unsaved catalog entry; "Connect with Zendesk" saves it and keeps it here.
  const [savedIntegration, setSavedIntegration] = useState<SelectedIntegration | null>(
    integration.id ? integration : null,
  );
  const [isManualConnectOpen, setIsManualConnectOpen] = useState(
    Boolean(integration.integration_data.auth_email),
  );
  // Once Zendesk OAuth is authorized, the manual API token route is locked.
  const isZendeskAuthorized =
    savedIntegration?.integration_data?.auth_type === 'oauth' &&
    savedIntegration?.integration_data?.auth_status === 'ok';
  const isManualConnectActive = isManualConnectOpen && !isZendeskAuthorized;
  const [addIntegration] = useAddIntegrationMutation();
  const [updateIntegration] = useUpdateIntegrationMutation();
  const [getZendeskConsentLinkQuery] = useLazyGetZendeskConsentLinkQuery();
  const { isCheckingAuthStatus, pollZendeskAuthorization } = useIntegrationAuthorizationPolling(id);
  const { handleSubmit, control, clearErrors } = useForm<ZendeskFormValues>({
    mode: 'onBlur',
    defaultValues: {
      subdomain: integration.integration_data.subdomain,
      auth_email: integration.integration_data.auth_email,
      api_token: integration.integration_data.api_token,
      via_email: integration.integration_data.via_email,
      via_chat: integration.integration_data.via_chat,
      credits: 0,
    },
  });

  // Keeps the add flow's store entry in sync, so its footer knows the integration now exists.
  const storeSavedIntegration = (data: SelectedIntegration) => {
    setSavedIntegration(data);
    if (!integration.id) {
      dispatch(setSelectedIntegration({ ...selectedIntegration, ...data }));
    }
  };

  const onConnectWithZendesk = async (values: ZendeskFormValues) => {
    let connectedIntegration = savedIntegration;

    loader.show();

    const body = {
      integration_type: integration.integration_type,
      integration_data: {
        subdomain: parseSubdomain(values.subdomain),
        via_chat: values.via_chat,
        via_email: values.via_email,
        credits: values.credits,
      },
    };
    if (!savedIntegration?.id) {
      const { data: addIntegrationData, error } = await addIntegration({ id, body });

      if (!error) {
        connectedIntegration = { ...integration, ...addIntegrationData };
        notification.success(`Integration ${addIntegrationData.integration_type} was added.`);
        storeSavedIntegration(connectedIntegration);
      }
    } else {
      const { data: updatedIntegration, error } = await updateIntegration({
        projectId: id,
        integrationId: savedIntegration.id,
        body,
      });

      if (!error) {
        connectedIntegration = { ...savedIntegration, ...updatedIntegration };
        notification.success(`Integration ${updatedIntegration.integration_type} was updated.`);
        storeSavedIntegration(connectedIntegration);
      }
    }

    if (
      connectedIntegration?.id &&
      connectedIntegration?.integration_data?.auth_type === 'oauth' &&
      connectedIntegration?.integration_data?.auth_status === 'needs_reauthorization'
    ) {
      const { data: consentLink } = await getZendeskConsentLinkQuery({
        project_id: id,
        integration_id: connectedIntegration.id,
        type: 'zendesk',
      });

      if (consentLink?.authorize_url) {
        window.open(consentLink.authorize_url);
        pollZendeskAuthorization(connectedIntegration.id, (authorizedIntegration) =>
          storeSavedIntegration({ ...connectedIntegration, ...authorizedIntegration }),
        );
      }
    }

    loader.hide();
  };

  // Runs the same validation as submit; the connect handler only fires for a valid form.
  const connectWithZendesk = handleSubmit(onConnectWithZendesk);

  const submit = async (values: ZendeskFormValues) => {
    const isNotAuthorized =
      savedIntegration?.integration_data?.auth_type === 'oauth' &&
      savedIntegration?.integration_data?.auth_status !== 'ok';

    // Manual API token credentials are submitted as-is; only the OAuth route runs the connect flow.
    if (!isManualConnectActive && (!savedIntegration?.id || isNotAuthorized)) {
      return onConnectWithZendesk(values);
    }

    const currentIntegration = savedIntegration ?? integration;
    const data = {
      ...currentIntegration,
      integration_data: {
        ...currentIntegration.integration_data,
        ...values,
        subdomain: parseSubdomain(values.subdomain),
      },
    };

    // An integration saved by "Connect with Zendesk" is updated here, not created again by the add flow.
    if (savedIntegration?.id && !integration.id) {
      await updateIntegration({ projectId: id, integrationId: savedIntegration.id, body: data });
      onSubmit(data, true);
      return;
    }

    onSubmit(data);
  };

  return (
    <form id="integration-form" onSubmit={handleSubmit(submit)}>
      {isCheckingAuthStatus && (
        <div
          className={
            'absolute flex items-center justify-center w-full h-full top-0 left-0 bg-ebony_opacity_30 backdrop-blur-sm z-50'
          }
        >
          <div
            className={
              'flex flex-col items-center gap-4 bg-light p-4 rounded-sm-5 text-sm font-semibold'
            }
          >
            <Spinner className={'!w-4 !border-[3px] !border-r-ebony_opacity_50'} />
            <p>Checking Zendesk Connection...</p>
          </div>
        </div>
      )}
      <div className={'input-container'}>
        <Input
          type={'text'}
          label={'Integration Type'}
          placeholder={'Integration Type'}
          value={integration.integration_type}
          readonly={true}
          isValid={true}
        />
      </div>
      <div className={'input-container'}>
        <Input
          type={'text'}
          label={'Credits'}
          placeholder={'Credits'}
          value={integration.integration_data.credits}
          readonly={true}
          isValid={true}
        />
      </div>
      <div className={'input-container'}>
        <Controller
          control={control}
          name="subdomain"
          rules={{
            validate: (value) => isLongerThanTwo(value) || (value ? 'Invalid Format' : 'Required'),
          }}
          render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
            <Input
              type={'text'}
              label={'Subdomain'}
              placeholder={'Subdomain'}
              value={value}
              invalidMessage={error?.message}
              isValid={!error}
              readonly={isZendeskAuthorized}
              onChange={(event) => onChange(parseSubdomain(event.currentTarget.value))}
              onBlur={onBlur}
              onFocus={() => clearErrors('subdomain')}
            />
          )}
        />
      </div>
      <div className={'mt-5 flex flex-col gap-2'}>
        <Button
          type={'button'}
          size={'sm'}
          onClick={connectWithZendesk}
          disabled={isManualConnectActive || isZendeskAuthorized}
          className={clsx(
            'w-full gap-2 px-8',
            isZendeskAuthorized
              ? 'bg-purple_150 text-purple_600 font-medium shadow-none disabled:opacity-100'
              : 'font-semibold bg-ebony text-white hover:bg-ebony_opacity_80 shadow-[0_1px_1px_0_rgba(5,5,13,0.25)]',
          )}
        >
          {isZendeskAuthorized ? (
            'Connected successfully with Zendesk'
          ) : (
            <>
              {'Connect with Zendesk'}
              <img src={ArrowLargeIcon} alt={''} width={16} height={16} />
            </>
          )}
        </Button>
        <p className={'text-center text-[13px] font-medium text-tertiary'}>
          You’ll be redirected to Zendesk to authorize access.
        </p>
        <div className={'flex items-center gap-2'}>
          <Separator className={'flex-1 bg-border_subtle'} />
          <span className={'text-[13px] font-medium text-tertiary'}>or</span>
          <Separator className={'flex-1 bg-border_subtle'} />
        </div>
        <Collapsible
          open={isManualConnectActive}
          onOpenChange={setIsManualConnectOpen}
          disabled={isZendeskAuthorized}
          triggerClassName={
            'group flex w-full items-center gap-2 rounded-lg border border-grey_border bg-white px-3 py-2 text-sm font-medium text-grey_750 transition-colors enabled:hover:border-purple_600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple_600/20 data-[state=open]:border-purple_600 disabled:cursor-not-allowed disabled:border-border_subtle disabled:text-grey_300'
          }
          trigger={
            <>
              <span className={'flex-1 text-center'}>Connect manually with API token</span>
              {isZendeskAuthorized ? (
                <img src={ChevronDownDisabledIcon} alt={''} width={16} height={16} />
              ) : (
                <img
                  src={ChevronDownIcon}
                  alt={''}
                  width={12.8}
                  height={12.8}
                  className={'transition-transform duration-200 group-data-[state=open]:rotate-180'}
                />
              )}
            </>
          }
          contentClassName={
            'overflow-hidden data-[state=open]:animate-collapsible-down data-[state=closed]:animate-collapsible-up'
          }
        >
          <div className={'pt-2'}>
            <div className={'input-container'}>
              <Controller
                control={control}
                name="auth_email"
                // API token credentials are only required when connecting manually.
                rules={{
                  validate: (value) =>
                    !isManualConnectActive ||
                    EMAIL_REGEX.test(value) ||
                    (value ? 'Invalid Format' : 'Required'),
                }}
                render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
                  <Input
                    type={'email'}
                    label={'Email'}
                    placeholder={'email@email.com'}
                    invalidMessage={error?.message}
                    value={value}
                    isValid={!error}
                    onChange={(event) => onChange(event.currentTarget.value)}
                    onBlur={onBlur}
                    onFocus={() => clearErrors('auth_email')}
                  />
                )}
              />
            </div>
            <div className={'input-container'}>
              <Controller
                control={control}
                name="api_token"
                rules={{
                  validate: (value) =>
                    !isManualConnectActive || isLongerThanTwo(value) || 'Invalid',
                }}
                render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
                  <Input
                    type={'password'}
                    label={'API Token'}
                    placeholder={'••••••••••••••••••••••••••••'}
                    invalidMessage={error?.message}
                    isValid={!error}
                    value={value}
                    onChange={(event) => onChange(event.currentTarget.value)}
                    onBlur={onBlur}
                    onFocus={() => clearErrors('api_token')}
                  />
                )}
              />
            </div>
          </div>
        </Collapsible>
      </div>
      <div className="flex sub-title integrate-via items-start md:flex-row flex-col gap-[16px] md:gap-[20px]">
        <h4>Integrate Via:</h4>
        <Controller
          control={control}
          name="via_email"
          render={({ field: { onChange, value } }) => (
            <Checkbox
              label={'Email'}
              checked={value}
              onChange={(event) => onChange(event.target.checked)}
            />
          )}
        />
        <Controller
          control={control}
          name="via_chat"
          render={({ field: { onChange, value } }) => (
            <Checkbox
              label={'Chat'}
              checked={value}
              onChange={(event) => onChange(event.target.checked)}
            />
          )}
        />
      </div>
    </form>
  );
};
